// The backoffice runs in ONE currency, set once under Einstellungen → Allgemein (operator,
// 02.10.2026). This file holds the only fallback in code — it applies while nothing is stored —
// and the helpers both server and client use. Every record keeps the currency it was issued in
// (`currency` on the invoice, abo, transaction): what a PSP or an import delivered stays.
//
// Not affected, by law: SEPA (always EUR) and the OSS threshold (stated in EUR).

export const DEFAULT_CURRENCY = 'EUR'

// ISO 4217 code? (Three capital letters the platform knows.)
export function isCurrencyCode(v: unknown): v is string {
    if (typeof v !== 'string' || !/^[A-Z]{3}$/.test(v)) return false
    try {
        new Intl.NumberFormat('de-DE', { style: 'currency', currency: v })
        return true
    } catch {
        return false
    }
}

// Decimal places of a currency as the platform knows them (EUR 2, JPY 0, KWD 3).
export function fractionDigits(code: string): number {
    try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits ?? 2
    } catch {
        return -1
    }
}

// What the backoffice can run in: a known code WITH TWO decimal places. Amounts go to and come from
// the PSPs in cents (×/÷ 100, StripeProvider/stripe.ts) — a JPY or KWD would be off by 100 or 10.
export function isSupportedCurrency(v: unknown): v is string {
    return isCurrencyCode(v) && fractionDigits(v) === 2
}

// The sign as the platform writes it in German („€" for EUR, „CHF", „$" …) — never a table here.
export function currencySymbol(code: string | undefined | null): string {
    const c = code || DEFAULT_CURRENCY
    try {
        const part = new Intl.NumberFormat('de-DE', { style: 'currency', currency: c })
            .formatToParts(0).find((p) => p.type === 'currency')
        return part?.value ?? c
    } catch {
        return c
    }
}

// The codes offered in the settings: known to the platform and with two decimal places.
export function knownCurrencies(): string[] {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    const all = intl.supportedValuesOf ? intl.supportedValuesOf('currency') : [DEFAULT_CURRENCY]
    return all.filter((c) => fractionDigits(c) === 2)
}
